/**
 * Movement-intent funnel. All input sources (keyboard cursors, classic
 * D-pad, nipplejs joystick, tap-to-walk pathfinder) write their per-direction
 * down/up state through `setIntent(dir, down, source)`. WorldScene reads
 * `intentRouter.dpadDown[dir]` — true iff *any* source has that direction
 * held.
 *
 * Step 2 of the touch-controls plan migrates the keyboard cursors + classic
 * D-pad off WorldScene's per-class `dpadDown` field onto this shared object.
 * Steps 3 (joystick) and 7 (pathfinder) layer on without touching the read
 * site in `tryProcessMovement`.
 *
 * Single module-level singleton: there's only ever one player, one set of
 * inputs, one scene that consumes intent. A class with constructors would
 * be over-engineered here.
 */

export type Dir = 'up' | 'down' | 'left' | 'right';
export type IntentSource = 'keyboard' | 'classic-dpad' | 'joystick' | 'tap-step';

const DIRS: readonly Dir[] = ['up', 'down', 'left', 'right'];
const SOURCES: readonly IntentSource[] = [
  'keyboard',
  'classic-dpad',
  'joystick',
  'tap-step',
];

function freshDirRecord(): Record<Dir, boolean> {
  return { up: false, down: false, left: false, right: false };
}

const sources: Record<IntentSource, Record<Dir, boolean>> = {
  keyboard: freshDirRecord(),
  'classic-dpad': freshDirRecord(),
  joystick: freshDirRecord(),
  'tap-step': freshDirRecord(),
};

function recompute(dir: Dir): boolean {
  for (const s of SOURCES) {
    if (sources[s][dir]) return true;
  }
  return false;
}

export const intentRouter = {
  /** OR-of-all-sources per direction. WorldScene's `tryProcessMovement`
   *  reads this in place of the previous `this.dpadDown[dir]`. */
  dpadDown: freshDirRecord(),

  /** Set or clear `dir` for one input source. Idempotent — repeated calls
   *  with the same value are no-ops. The shared `dpadDown` view is
   *  recomputed only for the affected direction. */
  setIntent(dir: Dir, down: boolean, source: IntentSource): void {
    if (sources[source][dir] === down) return;
    sources[source][dir] = down;
    this.dpadDown[dir] = recompute(dir);
  },

  /** True iff `source` currently holds any direction. Used by WorldScene
   *  to detect when a manual input (keyboard / classic D-pad / joystick)
   *  takes over from a tap-step auto-walk so the pathfinder can cancel
   *  itself rather than fighting the user's input. */
  isSourceActive(source: IntentSource): boolean {
    const ss = sources[source];
    return ss.up || ss.down || ss.left || ss.right;
  },

  /** Drop everything one source is holding. Used when a control mode tears
   *  down (e.g. switching DIRECT → COMMAND drops any held joystick state). */
  clearSource(source: IntentSource): void {
    let changed = false;
    for (const dir of DIRS) {
      if (sources[source][dir]) {
        sources[source][dir] = false;
        changed = true;
      }
    }
    if (!changed) return;
    for (const dir of DIRS) this.dpadDown[dir] = recompute(dir);
  },

  /** Drop every source's state. Called on Phaser SLEEP/WAKE so a battle
   *  that captured input mid-press doesn't leave sticky direction state
   *  when WorldScene resumes. */
  clearAll(): void {
    for (const s of SOURCES) {
      for (const dir of DIRS) sources[s][dir] = false;
    }
    for (const dir of DIRS) this.dpadDown[dir] = false;
  },
};
